import { Component, Input, OnInit, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { BaseService } from '../../services/base.service';
import { MatDialog } from '@angular/material/dialog';
import { CreateDealsFields } from '../../components/modals/create-deals-fields/create-deals-fields.component';
import { ICA } from './tab-deals-settings-fields.interface';
import { Subject, takeUntil } from 'rxjs';
import { IFunnel } from '../../interfaces';
import { CustomAttributesService } from '../../services/custom-attributes.service';
import { NotificationService } from '../../services/notifications.service';
import { ModalService } from '../../services/modal.service';
import { activeTranslations, LIST_CA } from '../../enum/constants';
import { DialogService } from '../../services/dialog.service';

@Component({
  selector: 'app-tab-funnel-fields',
  templateUrl: './tab-deals-settings-fields.component.html',
  styleUrls: ['./tab-deals-settings-fields.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class TabDealsSettingsFieldsComponent implements OnInit, OnDestroy {
  // Destroy
  private readonly destroy$: Subject<null> = new Subject<null>();

  // Enums and other imports
  protected readonly LIST_CA = LIST_CA;

  // Inputs
  @Input() funnelData!: IFunnel;

  // Vars
  public items: ICA[] = [];

  public isLoader: boolean = false;

  constructor(
    public readonly dialog: MatDialog,
    public readonly baseService: BaseService,
    public readonly notificationService: NotificationService,
    public readonly customAttributesService: CustomAttributesService,
    public readonly modalService: ModalService,
    public readonly dialogService: DialogService,
  ) {}

  ngOnInit() {
    this.onGetAllFields();
  }

  ngOnDestroy(): void {
    this.destroy$.next(null);
    this.destroy$.complete();
  }

  openModal() {
    const dialogData = {
      title: 'create_field',
      translate: true,
      data: {
        funnel_id: this.funnelData.id,
        position: this.items[this.items.length - 1]?.position || 0,
      },
      component: CreateDealsFields,
      width: '30vw',
    };

    this.modalService.openModal(dialogData).subscribe((result) => {
      if (result) this.onGetAllFields();
    });
  }

  public drop(event: CdkDragDrop<string[]>): void {
    if (this.items[event.currentIndex].id === this.items[event.previousIndex].id) return;

    const position: number = this.calcMovedPosition(event.currentIndex, event.previousIndex);

    moveItemInArray(this.items, event.previousIndex, event.currentIndex);

    this.customAttributesService
      .updateCA({ position: position }, this.items[event.currentIndex].id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.notificationService.showSuccess(
            '',
            activeTranslations.notifications_texts.success.successfully_moved,
          );
          this.onGetAllFields(true);
        },
        error: () => {
          this.notificationService.showError(
            '',
            activeTranslations.notifications_texts.error.error_update_field_position,
          );
        },
      });
  }

  private calcMovedPosition(cp: number, pp: number): number {
    if (cp === 0) return this.items[0].position / 2;

    if (cp === this.items.length - 1) {
      const lastPosition: number = this.items[this.items.length - 1].position;

      return (lastPosition + (lastPosition + 1000)) / 2;
    }

    if (cp > pp) return (this.items[cp].position + this.items[cp + 1].position) / 2;
    if (cp < pp) return (this.items[cp].position + this.items[cp - 1].position) / 2;

    return (this.items[cp + 1].position + this.items[cp - 1].position) / 2;
  }

  public onGetAllFields(isPositionUpdate: boolean = false) {
    if (!isPositionUpdate) this.isLoader = true;

    const payload = {
      funnel_id: this.funnelData?.id,
      page: 1,
      take: 50,
    };

    this.customAttributesService
      .getAllCA(payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          this.items = [...res.items];
          this.isLoader = false;
        },
        error: () => {
          this.notificationService.showError(
            '',
            activeTranslations.notifications_texts.error.error_getting_field,
          );
          this.isLoader = false;
        },
      });
  }

  updateCA(item: ICA) {
    this.customAttributesService
      .updateCA({ is_view: item.is_view }, item.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.onGetAllFields();
        },
        error: () => {
          this.notificationService.showError(
            '',
            activeTranslations.notifications_texts.error.error_update_field,
          );
        },
      });
  }

  deleteCA(item: ICA) {
    const dialogRef = this.dialogService.openConfirm({
      text: 'dialogs.tab-deals-settings-fields',
    });
    dialogRef.subscribe({
      next: (result: any) => {
        if (result) {
          this.customAttributesService
            .deleteCA(item.id)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
              next: (res: any) => {
                this.onGetAllFields();
              },
              error: () => {
                this.notificationService.showError(
                  '',
                  activeTranslations.notifications_texts.error.error_delete_field,
                );
              },
            });
        }
      },
      error: () => {
        this.notificationService.showError(
          '',
          activeTranslations.notifications_texts.error.error_delete,
        );
      },
    });
  }

  edit(item: ICA) {
    const dialogData = {
      title: 'change_field',
      translate: true,
      data: {
        funnel_id: this.funnelData.id,
        field: item,
      },
      component: CreateDealsFields,
      width: '30vw',
    };

    this.modalService.openModal(dialogData).subscribe((result) => {
      if (result) {
        this.onGetAllFields();
      }
    });
  }

  findByType(type: string) {
    return LIST_CA.find((v) => v.value === type);
  }
}
